import { HttpClient, HttpParams } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import { BehaviorSubject, tap } from 'rxjs'
import { environment } from '../../../../environments/environment'

export interface DiagnosticReasonCount {
  code: string
  label: string
  count: number
}

export interface DiagnosticAffair {
  affairId: number
  name: string | null
  groupingName: string | null
  address: string | null
  postalCode: string | null
  locality: string | null
  latitude: number | null
  longitude: number | null
  mapsQuery: string | null
  redirectUrl: string | null
  code: string | null
  label: string | null
  comment: string | null
  detail: any
  finalizedAt: string | null
  planningDeadline?: string | null
}

export interface DiagnosticRunInfo {
  id: number
  status: string
  affairsCount: number | null
  affairsWithSlotsCount: number | null
  affairsWithoutSlots: number
  completedAt: string | null
  diagnosticsFinalizedAt: string | null
}

export interface DiagnosticListResponse {
  run: DiagnosticRunInfo
  reasons: DiagnosticReasonCount[]
  affairs: DiagnosticAffair[]
}

export interface DiagnosticEvent {
  id: number
  stage: string
  code: string
  label: string
  scanDate: string | null
  clusterIndex: number | null
  isFinal: boolean
  detail: any
  createdAt: string
}

export interface DiagnosticDetailResponse {
  runId: number
  affair: DiagnosticAffair
  events: DiagnosticEvent[]
}

export interface AffairDiagnosticResult {
  runId: number
  affair: DiagnosticAffair
  hasSlot: boolean
}

@Injectable({
  providedIn: 'root',
})
export class OptimizationDiagnosticService {
  private http = inject(HttpClient)
  private apiUrl = environment.planifyApiUrl

  private loading = new BehaviorSubject<boolean>(false)
  private detailLoading = new BehaviorSubject<boolean>(false)

  public loading$ = this.loading.asObservable()
  public detailLoading$ = this.detailLoading.asObservable()

  /** Liste des affaires sans créneau d'un run (dernier run complété si runId omis). */
  getDiagnostics(runId?: number) {
    this.loading.next(true)
    let params = new HttpParams()
    if (runId) {
      params = params.set('runId', runId)
    }
    return this.http
      .get<DiagnosticListResponse>(
        `${this.apiUrl}/affairs/optimization/diagnostics`,
        { params }
      )
      .pipe(tap({ complete: () => this.loading.next(false) }))
  }

  /**
   * Lance à la demande le diagnostic d'une affaire restée sans créneau (sonde de
   * relaxation VROOM) et renvoie sa raison dominante. Indépendant d'une collecte
   * de diagnostic préalable du run.
   */
  runAffairDiagnostic(affairId: number, runId?: number) {
    return this.http.post<AffairDiagnosticResult>(
      `${this.apiUrl}/affairs/optimization/diagnostics/${affairId}`,
      runId ? { runId } : {}
    )
  }

  /** Chronologie de diagnostic d'une affaire pour un run donné. */
  getAffairDiagnostic(affairId: number, runId?: number) {
    this.detailLoading.next(true)
    let params = new HttpParams()
    if (runId) {
      params = params.set('runId', runId)
    }
    return this.http
      .get<DiagnosticDetailResponse>(
        `${this.apiUrl}/affairs/optimization/diagnostics/${affairId}`,
        { params }
      )
      .pipe(tap({ complete: () => this.detailLoading.next(false) }))
  }
}
